import type {
  CreateSoftHoldRequest,
  DocumentAcceptanceEvidence,
} from "@fm/api";

export interface BuildSoftHoldRequestParams {
  vehicleId: string;
  startDate: string;
  endDate: string;
  customerEmail: string;
  customerName: string;
  customerPhone: string;
  depositAcknowledged: boolean;
  selectedAddonIds: string[];
  selectedInsuranceOptionIds: string[];
  deliveryOptions: unknown;
  adults: number;
  children: number;
  infants: number;
  pets: number;
  /**
   * Version `public_id`s (UUIDs) of every mandatory document the customer
   * explicitly checked in `AcceptanceDocumentsSection`. Sent at the top
   * level of the request, never nested in `metadata` — the server reads it
   * from `params["accepted_document_version_ids"]`
   * (`SoftHoldController.create/2`).
   */
  acceptedDocumentVersionIds: string[];
  /**
   * What the customer typed or drew inside each document, keyed by version
   * `public_id`. Top level like `acceptedDocumentVersionIds`; omitted when
   * empty. Temporary capture, see odd/tasks/document-form-capture.md.
   */
  documentFormData?: Record<string, Record<string, string>>;
  /** Client-side hashes and context per document (cross-check only). */
  documentEvidence?: Record<string, DocumentAcceptanceEvidence>;
  /**
   * Typed answers to the fleet's custom booking questions, keyed by the
   * catalog question's integer id (as a string). Travels at the top level
   * (`params["screening_answers"]`) and is validated server-side against
   * whichever flow's catalog is active for the vehicle
   * (`Core.Fleet.ScreeningQuestions.validate_answers/3`) — no longer
   * Request-to-Book-only, since a fleet can configure questions for either
   * flow. Omitted (not an empty object) when the flow's active catalog is
   * empty. The renter's free-text message to the fleet is just another
   * catalog question (backfilled for every fleet as a `textarea` question
   * labeled "Message to the fleet") and is sent through this map like any
   * other answer — there is no separate `renter_message` field any more.
   */
  screeningAnswers?: Record<string, string | number | boolean>;
}

/**
 * Builds the `POST /v1/soft-holds` request body. Pulled out of
 * `initializeCheckout` as a pure function so the payload shape — in
 * particular, that accepted mandatory documents travel as
 * `accepted_document_version_ids` — is testable without mounting
 * `CheckoutContainer` and its Stripe/react-query dependency tree.
 */
export function buildSoftHoldRequest(
  params: BuildSoftHoldRequestParams,
): CreateSoftHoldRequest {
  const request: CreateSoftHoldRequest = {
    vehicle_id: params.vehicleId,
    start_date: params.startDate,
    end_date: params.endDate,
    customer_email: params.customerEmail,
    total_amount: "0.00", // Ignored by backend
    deposit_acknowledged: params.depositAcknowledged,
    accepted_document_version_ids: params.acceptedDocumentVersionIds,
    metadata: {
      customer_name: params.customerName,
      customer_phone: params.customerPhone,
      selected_addon_ids: params.selectedAddonIds,
      selected_insurance_option_ids: params.selectedInsuranceOptionIds,
      delivery_options: params.deliveryOptions,
      adults: params.adults,
      children: params.children,
      infants: params.infants,
      pets: params.pets,
      payment_method_type: "card", // Default, will be updated when payment method selected
    },
  };

  if (params.documentFormData && Object.keys(params.documentFormData).length) {
    request.document_form_data = params.documentFormData;
  }

  if (params.documentEvidence && Object.keys(params.documentEvidence).length) {
    request.document_evidence = params.documentEvidence;
  }

  if (params.screeningAnswers) {
    request.screening_answers = params.screeningAnswers;
  }

  return request;
}
